import { audioRecordingModule } from "@/modules/audio-recording";
import { logger } from "@/modules/logger";
import { DefaultUploadManager } from "@/modules/audio-recording/legacy";
import { TranscriptArrayItem } from "@/modules/audio-recording/legacy";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { LocalAudioRecordingBlobsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-blobs";
import { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";
import { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording";
import type { Uuid } from "@/domains/global/identifiers";
import { AudioRecordingUploadProgressTracker } from "@/store/audio/uploading/audio-uploading.types";
import { uuidModule } from "@/modules/uuid";
import {
  enqueueRecordingFailureModal,
  RecordingFailureReason,
} from "@/store/audio/recording-failure-modal";
import { isRecordingTooShort } from "@/store/audio-recordings";
import type { ModalsStore } from "@/store/modals/ModalsStore";
import type { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";

type UploadJobArgs = {
  audioRecordingId: Uuid;
};

type UploadDependencies = {
  audioRecordings: AudioRecordingRepository;
  blobs: LocalAudioRecordingBlobsRepository;
  transcripts: LocalAudioRecordingTranscriptTurnsRepository;
  progressTracker: AudioRecordingUploadProgressTracker;
  modals: ModalsStore;
  navigation: AppStoreNavigationStore;
};

export class AudioRecordingUploader {
  private readonly deps: UploadDependencies;
  private readonly queuedJobs: UploadJobArgs[] = [];
  private readonly queuedJobKeys = new Set<string>();
  private currentUploader: DefaultUploadManager | null = null;
  private isRunning = false;
  private readonly onUploadComplete: ((job: UploadJobArgs) => void) | null;

  constructor(
    deps: UploadDependencies,
    callbacks?: {
      onUploadComplete?: (job: UploadJobArgs) => void;
    }
  ) {
    this.deps = deps;
    this.onUploadComplete = callbacks?.onUploadComplete ?? null;
  }

  enqueue(job: UploadJobArgs): void {
    const jobKey = this.buildJobKey(job);
    if (this.queuedJobKeys.has(jobKey)) {
      return;
    }

    this.queuedJobKeys.add(jobKey);
    this.queuedJobs.push(job);
    void this.runQueue();
  }

  pause(): void {
    this.currentUploader?.pause();
  }

  resume(): void {
    this.currentUploader?.resume();
  }

  isActive(): boolean {
    return this.isRunning;
  }

  private async runQueue(): Promise<void> {
    if (this.isRunning) return;
    this.isRunning = true;
    try {
      while (this.queuedJobs.length) {
        const job = this.queuedJobs[0];
        const jobKey = this.buildJobKey(job);

        try {
          await this.processUploadJob(job);
          this.onUploadComplete?.(job);
        } catch (error) {
          logger.error({
            message: "[AudioRecordingUploader] upload failure",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
        } finally {
          this.queuedJobs.shift();
          this.queuedJobKeys.delete(jobKey);
        }
      }
    } finally {
      this.isRunning = false;
    }
  }

  private async processUploadJob({ audioRecordingId }: UploadJobArgs): Promise<void> {
    const { audioRecordings, blobs, transcripts, progressTracker, modals, navigation } = this.deps;
    let didError = false;

    const recording = await audioRecordings.getById({ id: audioRecordingId });

    const showUploadFailureModal = ({ associatedNoteId }: { associatedNoteId?: Uuid | null }) =>
      enqueueRecordingFailureModal({
        modals,
        navigation,
        reason: RecordingFailureReason.FailedToUpload,
        associatedNoteId,
      });

    if (!recording) {
      logger.error({
        message: "[AudioRecordingUploader] Missing audio recording",
        info: { audioRecordingId },
      });

      progressTracker.setProgress({
        audioRecordingId,
        state: {
          uploadProgressPercent: null,
          isUploading: false,
          errorMessage: "Recording not found",
        },
      });

      showUploadFailureModal({ associatedNoteId: null });
      return;
    }

    const pendingBlobs = (
      await blobs.getAllByRecordingId({
        associatedAudioRecordingId: audioRecordingId,
      })
    ).filter(record => !record.isUploaded);

    if (!pendingBlobs.length) {
      const isTooShort = isRecordingTooShort(recording.data);
      const errorMessage = isTooShort ? "No audio content detected" : "Recording audio is missing";

      await audioRecordings.markAsUploadErrored({
        id: audioRecordingId,
        errorMessage,
      });

      progressTracker.setProgress({
        audioRecordingId,
        state: {
          uploadProgressPercent: null,
          isUploading: false,
          errorMessage,
        },
      });

      if (isTooShort) {
        logger.info({
          message: "[AudioRecordingUploader] Recording too short - no audio content",
          info: { audioRecordingId },
        });
        enqueueRecordingFailureModal({
          modals,
          navigation,
          reason: RecordingFailureReason.NoContentDetected,
          associatedNoteId: recording.data.associated_note_id,
        });
      } else {
        logger.error({
          message: "[AudioRecordingUploader] No audio blobs to upload",
          info: { audioRecordingId },
        });
        showUploadFailureModal({ associatedNoteId: recording.data.associated_note_id });
      }
      return;
    }

    const transcriptRecords = await transcripts.getAllByRecordingId({
      associatedAudioRecordingId: audioRecordingId,
    });

    const { transcriptArray, transcriptText } = buildTranscriptPayload(transcriptRecords);
    const combinedBlob = await audioRecordingModule.transformBlobToPlayableFormat({
      blobs: pendingBlobs.map(record => record.rawBlob),
    });

    if (!combinedBlob) {
      const errorMessage = "Recording audio is unavailable";
      await audioRecordings.markAsUploadErrored({
        id: audioRecordingId,
        errorMessage,
      });
      progressTracker.setProgress({
        audioRecordingId,
        state: {
          uploadProgressPercent: null,
          isUploading: false,
          errorMessage,
        },
      });

      logger.error({
        message: "[AudioRecordingUploader] Unable to assemble playable blob",
        info: { audioRecordingId },
      });
      showUploadFailureModal({ associatedNoteId: recording.data.associated_note_id });
      return;
    }

    await audioRecordings.markAsUploading({ id: audioRecordingId });

    progressTracker.setProgress({
      audioRecordingId,
      state: {
        uploadProgressPercent: 0,
        isUploading: true,
        errorMessage: null,
      },
    });

    const uploader = new DefaultUploadManager({
      minUploadDurationMs: 1000,
      progressUpdateIntervalMs: 50,
    });
    this.currentUploader = uploader;

    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });

    try {
      const result = await uploader.start(
        combinedBlob,
        {
          capturedAudioId,
          associatedAudioRecordingId: audioRecordingId,
          preferredMimeType: combinedBlob.type || undefined,
          transcriptArray,
          transcriptText,
        },
        {
          onProgress: percent => {
            progressTracker.setProgress({
              audioRecordingId,
              state: {
                uploadProgressPercent: Math.round(percent),
                isUploading: true,
                errorMessage: null,
              },
            });
          },
        }
      );

      if (result.success) {
        await Promise.all(
          pendingBlobs.map(record =>
            blobs.update({
              associatedAudioRecordingId: audioRecordingId,
              blobIndex: record.blobIndex,
              data: { isUploaded: true },
            })
          )
        );

        await audioRecordings.markAsUploadCompleted({ id: audioRecordingId });
      } else {
        const errorMessage = result.error ?? "Upload failed";
        await audioRecordings.markAsUploadErrored({
          id: audioRecordingId,
          errorMessage,
        });
        didError = true;
        progressTracker.setProgress({
          audioRecordingId,
          state: {
            uploadProgressPercent: null,
            isUploading: false,
            errorMessage,
          },
        });
        showUploadFailureModal({ associatedNoteId: recording.data.associated_note_id });
      }
    } catch (error) {
      didError = true;
      const message = error instanceof Error ? error.message : String(error);
      await audioRecordings.markAsUploadErrored({
        id: audioRecordingId,
        errorMessage: message,
      });
      progressTracker.setProgress({
        audioRecordingId,
        state: {
          uploadProgressPercent: null,
          isUploading: false,
          errorMessage: message,
        },
      });
      showUploadFailureModal({ associatedNoteId: recording?.data.associated_note_id ?? null });
      throw error;
    } finally {
      if (!didError) {
        progressTracker.clearProgress({ audioRecordingId });
      }

      this.currentUploader = null;
    }
  }

  private buildJobKey({ audioRecordingId }: UploadJobArgs): string {
    return audioRecordingId;
  }

  resetState() {
    this.queuedJobs.splice(0);
    this.queuedJobKeys.clear();
    this.currentUploader = null;
    this.isRunning = false;
  }
}

function buildTranscriptPayload(records: LocalAudioRecordingTranscriptTurnRecord[]): {
  transcriptArray: TranscriptArrayItem[];
  transcriptText: string;
} {
  if (!records.length) {
    return { transcriptArray: [], transcriptText: "" };
  }

  const transcriptArray: TranscriptArrayItem[] = records.map(record => {
    const words = record.content.words ?? [];
    const transcript = words
      .map(word => word.text ?? "")
      .join(" ")
      .trim();
    const sortKey = `${record.transcriptTurnIndex}`.padStart(6, "0");
    return {
      sort_key: sortKey,
      transcript,
      channel: record.channel ?? 0,
    };
  });

  const transcriptText = transcriptArray
    .map(item => item.transcript)
    .filter(Boolean)
    .join(" ")
    .trim();

  return { transcriptArray, transcriptText };
}
